import React, { useState, useEffect } from 'react'
import { LoaderBar, PageHeader } from 'components'
import { AssetTypeList, AssetTypeAddEditModal } from 'page-components/asset/types'
import { DesktopMode, MobileMode } from 'layout'
import { AppPrimaryButton } from 'elements'
import { useAssetCategory } from 'hooks/asset-manager'
import { useAuth } from 'context/user'

export const AssetTypeListScreen = () => {
    const { user } = useAuth()
    const {
        categories,
        loading,
        fetchCategories,
        getCategory,
        saveCategory,
        deleteCategory,
    } = useAssetCategory()

    const [modalData, setModalData] = useState({})
    const [openModal, setOpenModal] = useState(false)
    const [processing, setProcessing] = useState('')
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: 10,
    })
    const categoriesForUi = Array.isArray(categories) ? categories : []
    const totalCount = categoriesForUi.length

    useEffect(() => {
        if (user?.CompanyId) {
            fetchCategories()
        }
    }, [user?.CompanyId, fetchCategories])

    const pageChanged = (newFilter) => {
        setFilter(newFilter)
        // TODO: Replace with actual API call
    }

    const handleAddType = () => {
        setModalData({})
        setOpenModal(true)
    }

    const handleEdit = async (item) => {
        const categoryId = item.AssetCategoryId || item.id
        const categoryDetail = categoryId ? await getCategory(categoryId) : null
        const source = categoryDetail || item
        setModalData({
            AssetCategoryId: source.AssetCategoryId || source.id,
            type: source.CategoryName || source.type,
            description: source.Description || source.description,
            IsActive: source.IsActive !== false,
        })
        setOpenModal(true)
    }

    // Transform API data to match component expectations
    const transformedData = categoriesForUi.map(cat => ({
        id: cat.AssetCategoryId,
        AssetCategoryId: cat.AssetCategoryId,
        type: cat.CategoryName,
        CategoryName: cat.CategoryName,
        description: cat.Description || '',
        Description: cat.Description || '',
        IsActive: cat.IsActive,
    }))

    const handleRemove = async (id) => {
        setProcessing('Processing')
        try {
            const success = await deleteCategory(id)
            if (success) {
                await fetchCategories()
            }
        } catch (error) {
            console.error('Error removing asset type:', error)
        } finally {
            setProcessing('')
        }
    }

    const handleClose = async (formData) => {
        if (formData) {
            setProcessing('Saving')
            try {
                const categoryData = {
                    AssetCategoryId: modalData.AssetCategoryId || 0,
                    CategoryName: formData.type || formData.CategoryName,
                    Description: formData.description || formData.Description || '',
                    IsActive: formData.IsActive !== false,
                }

                const result = await saveCategory(categoryData)
                const isSaveSuccess =
                    result !== null && result !== undefined && result !== false
                if (isSaveSuccess) {
                    setOpenModal(false)
                    setModalData({})
                    await fetchCategories()
                    return
                }
            } catch (error) {
                console.error('Error saving asset type:', error)
            } finally {
                setProcessing('')
            }
        }
        setOpenModal(false)
        setModalData({})
    }

    return (
        <>
            {/* Desktop Layout */}
            <DesktopMode>
                <div className="row mb-3">
                    <div className="col-8">
                        <PageHeader header="Asset Types" />
                    </div>
                    <div className="col-4">
                        <div style={{ marginTop: '8px' }}>
                            <AppPrimaryButton
                                type="button"
                                onClick={handleAddType}
                                fluid
                            >
                                Add Type
                            </AppPrimaryButton>
                        </div>
                    </div>
                </div>
            </DesktopMode>

            {/* Mobile Layout */}
            <MobileMode>
                <div className="row mb-3">
                    <div className="col-12">
                        <PageHeader header="Asset Types" />
                    </div>
                    <div className="col-12 mt-2">
                        <AppPrimaryButton
                            type="button"
                            onClick={handleAddType}
                            fluid
                        >
                            Add Type
                        </AppPrimaryButton>
                    </div>
                </div>
            </MobileMode>

            <div className="col-12">
                <AssetTypeAddEditModal
                    open={openModal}
                    onClose={handleClose}
                    data={modalData}
                    loading={processing}
                />
            </div>

            <div className="my-3">
                {loading && <LoaderBar content="Loading" />}
                {!loading && (
                    <AssetTypeList
                        data={transformedData}
                        filter={filter}
                        pageChanged={pageChanged}
                        totalCount={totalCount}
                        onEdit={handleEdit}
                        onRemove={handleRemove}
                    />
                )}
            </div>
        </>
    )
}
